import React, { useState, useEffect } from "react";
import "../styles/SupportPage.scss";

const API_URL = "https://iracing6-backend.herokuapp.com/api";

const SupportPage = () => {
        const [isLoggedIn, setIsLoggedIn] = useState(false);
        const [isPaidUser, setIsPaidUser] = useState(false);
        const [subscriptionStatus, setSubscriptionStatus] = useState(null);
        const [loading, setLoading] = useState(true);
        const [actionLoading, setActionLoading] = useState(false);
        const [message, setMessage] = useState({ type: "", text: "" });

        useEffect(() => {
                const token = localStorage.getItem("token");
                setIsLoggedIn(!!token);

                // Check URL params for success/cancel from Stripe redirect
                const urlParams = new URLSearchParams(window.location.search);
                if (urlParams.get("success") === "true") {
                        setMessage({ type: "success", text: "Subscription successful! Welcome to iRacingData Pro!" });
                        // Clear URL params
                        window.history.replaceState({}, document.title, "/support");
                } else if (urlParams.get("canceled") === "true") {
                        setMessage({ type: "info", text: "Checkout was canceled. You can try again when you're ready." });
                        window.history.replaceState({}, document.title, "/support");
                }

                if (token) {
                        fetchSubscriptionStatus(token);
                } else {
                        setLoading(false);
                }
        }, []);

        const fetchSubscriptionStatus = async (token) => {
                try {
                        const response = await fetch(`${API_URL}/stripe/subscription-status`, {
                                headers: {
                                        Authorization: `Bearer ${token}`,
                                },
                        });
                        if (response.ok) {
                                const data = await response.json();
                                setIsPaidUser(data.isPaidUser);
                                setSubscriptionStatus(data.subscriptionStatus);
                                // Update localStorage
                                localStorage.setItem("isPaidUser", data.isPaidUser);
                        }
                } catch (err) {
                        console.error("Error fetching subscription status:", err);
                } finally {
                        setLoading(false);
                }
        };

        const handleSubscribe = async () => {
                const token = localStorage.getItem("token");
                if (!token) {
                        setMessage({ type: "error", text: "Please log in to subscribe." });
                        return;
                }

                setActionLoading(true);
                setMessage({ type: "", text: "" });

                try {
                        const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
                                method: "POST",
                                headers: {
                                        Authorization: `Bearer ${token}`,
                                        "Content-Type": "application/json",
                                },
                        });

                        const data = await response.json();

                        if (response.ok && data.url) {
                                window.location.href = data.url;
                        } else {
                                setMessage({ type: "error", text: data.message || "Failed to start checkout." });
                        }
                } catch (err) {
                        console.error("Error creating checkout session:", err);
                        setMessage({ type: "error", text: "An error occurred. Please try again." });
                } finally {
                        setActionLoading(false);
                }
        };

        const handleManageSubscription = async () => {
                const token = localStorage.getItem("token");
                if (!token) return;

                setActionLoading(true);
                setMessage({ type: "", text: "" });

                try {
                        const response = await fetch(`${API_URL}/stripe/create-portal-session`, {
                                method: "POST",
                                headers: {
                                        Authorization: `Bearer ${token}`,
                                        "Content-Type": "application/json",
                                },
                        });

                        const data = await response.json();

                        if (response.ok && data.url) {
                                window.location.href = data.url;
                        } else {
                                setMessage({ type: "error", text: data.message || "Failed to open subscription portal." });
                        }
                } catch (err) {
                        console.error("Error creating portal session:", err);
                        setMessage({ type: "error", text: "An error occurred. Please try again." });
                } finally {
                        setActionLoading(false);
                }
        };

        const renderSubscriptionSection = () => {
                if (loading) {
                        return <p className="loading-text">Loading...</p>;
                }

                if (!isLoggedIn) {
                        return (
                                <div className="subscribe-section">
                                        <div className="pro-benefits">
                                                <h3>iRacingData Pro Benefits</h3>
                                                <ul>
                                                        <li>Ad-free on the whole site</li>
                                                        <li>Pro Dashboard</li>
                                                        <li>Scheduled Emails</li>
                                                </ul>
                                        </div>
                                        <div className="price-info">
                                                <span className="price">$1.99</span>
                                                <span className="period">/month</span>
                                        </div>
                                        <button className="subscribe-button" onClick={() => window.location.href = "/login"}>
                                                Log in to Subscribe
                                        </button>
                                        <p className="cancel-info">Cancel anytime. No commitment.</p>
                                </div>
                        );
                }

                if (isPaidUser && subscriptionStatus === "active") {
                        return (
                                <div className="active-subscription">
                                        <p className="status-badge active">Pro Member</p>
                                        <p>Thank you for supporting iRacingData! You have full ad-free access.</p>
                                        <button className="manage-subscription-button" onClick={handleManageSubscription} disabled={actionLoading}>
                                                {actionLoading ? "Loading..." : "Manage Subscription"}
                                        </button>
                                </div>
                        );
                }

                if (subscriptionStatus === "past_due") {
                        return (
                                <div className="subscription-issue">
                                        <p className="status-badge past-due">Payment Issue</p>
                                        <p>Your subscription payment failed. Please update your payment method.</p>
                                        <button className="manage-subscription-button" onClick={handleManageSubscription} disabled={actionLoading}>
                                                {actionLoading ? "Loading..." : "Update Payment Method"}
                                        </button>
                                </div>
                        );
                }

                return (
                        <div className="subscribe-section">
                                <div className="pro-benefits">
                                        <h3>iRacingData Pro Benefits</h3>
                                        <ul>
                                                <li>Ad-free on the whole site</li>
                                                <li>Pro Dashboard</li>
                                                <li>Scheduled Emails</li>
                                        </ul>
                                </div>
                                <div className="price-info">
                                        <span className="price">$1.99</span>
                                        <span className="period">/month</span>
                                </div>
                                <button className="subscribe-button" onClick={handleSubscribe} disabled={actionLoading}>
                                        {actionLoading ? "Loading..." : "Subscribe to Pro"}
                                </button>
                                <p className="cancel-info">Cancel anytime. No commitment.</p>
                        </div>
                );
        };

        return (
                <div className="support-page">
                        <h2>Support The Site</h2>

                        {message.text && <div className={`subscription-message ${message.type}`}>{message.text}</div>}

                        <div className="subscription-section">{renderSubscriptionSection()}</div>

                        <h3 className="pro-dash-label">Pro Dashboard:</h3>
                        <img
                                src="/assets/images/pro-dash-example1.png"
                                alt="Pro Dashboard example"
                                className="pro-dash-preview"
                        />
                </div>
        );
};

export default SupportPage;
